import {
  OrganizationsService,
  type ProgramPublic,
  ProgramsService,
} from "@/client"
import type { MembershipPublic, ProgramStatusEnum } from "@/client/types.gen"
import { ProgramEnrollments } from "@/components/Programs/ProgramEnrollments"
import { ProgramQuizzes } from "@/components/Programs/ProgramQuizzes"
import useAuth from "@/hooks/useAuth"
import {
  Alert,
  Badge,
  Box,
  HStack,
  Heading,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { PageShell, PageSkeleton } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"

export const Route = createFileRoute("/_layout/programs/$id")({
  component: ProgramDetailsPage,
})

function ProgramDetailsPage() {
  const { id } = Route.useParams()
  const { user } = useAuth()

  const {
    data: program,
    isLoading,
    error,
  } = useQuery<ProgramPublic>({
    queryKey: ["program", id],
    queryFn: () => ProgramsService.getProgram({ programId: id }),
  })

  const breadcrumbOverrides = [
    { label: program?.name ?? "Program Details", isCurrent: true },
  ]
  const breadcrumbs = useRouteBreadcrumbs(
    "programs.detail",
    breadcrumbOverrides,
  )

  const { data: memberships } = useQuery<MembershipPublic[]>({
    queryKey: ["myMemberships", user?.id],
    queryFn: () => OrganizationsService.getMyMemberships(),
    enabled: Boolean(user && !user.is_superuser),
    staleTime: 5 * 60 * 1000,
  })

  const statusColor = (status: ProgramStatusEnum | undefined) => {
    switch (status) {
      case "active":
        return "green"
      case "inactive":
        return "orange"
      case "archived":
        return "gray"
      default:
        return "gray"
    }
  }

  const formatDate = (date?: string | null) => {
    if (!date) return "—"
    const parsed = new Date(date)
    if (Number.isNaN(parsed.getTime())) {
      return "—"
    }
    return parsed.toLocaleDateString()
  }

  if (isLoading) {
    return (
      <PageShell
        title="Program Details"
        description="View program timeline, enrollment status, and linked quizzes."
        breadcrumbs={breadcrumbs}
      >
        <PageSkeleton variant="detail" rows={6} />
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell
        title="Program Details"
        description="View program timeline, enrollment status, and linked quizzes."
        breadcrumbs={breadcrumbs}
      >
        <Alert.Root status="error">
          <Alert.Title>Unable to load program</Alert.Title>
          <Alert.Description>
            {(error as Error).message || "Please try again later."}
          </Alert.Description>
        </Alert.Root>
      </PageShell>
    )
  }

  if (!program) {
    return (
      <PageShell
        title="Program Details"
        description="View program timeline, enrollment status, and linked quizzes."
        breadcrumbs={breadcrumbs}
      >
        <Text>Program not found.</Text>
      </PageShell>
    )
  }

  const canManageEnrollments = Boolean(
    user?.is_superuser ||
      memberships?.some(
        (membership) =>
          membership.org_id === program.org_id && membership.role === "admin",
      ),
  )

  return (
    <PageShell
      title={program.name}
      description="Review program information and manage enrollments."
      breadcrumbs={breadcrumbs}
    >
      <Stack gap={6} data-testid="program-details-page">
        <Box borderWidth="1px" borderRadius="md" p={6} bg="white">
          <Stack gap={3}>
            <HStack justify="space-between" align="flex-start">
              <Heading size="lg">{program.name}</Heading>
              <Badge
                colorPalette={
                  statusColor(program.status) as "green" | "orange" | "gray"
                }
              >
                {program.status ?? "active"}
              </Badge>
            </HStack>
            <Text color="gray.600">
              {program.description ||
                "No description provided for this program."}
            </Text>
            <Separator />
            <HStack gap={8} flexWrap="wrap">
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Start date
                </Text>
                <Text fontWeight="medium">
                  {formatDate(program.start_date)}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  End date
                </Text>
                <Text fontWeight="medium">{formatDate(program.end_date)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Enrolled users
                </Text>
                <Text fontWeight="medium">{program.enrolled_count ?? 0}</Text>
              </Box>
            </HStack>
          </Stack>
        </Box>

        <Stack gap={4}>
          {canManageEnrollments ? (
            <ProgramEnrollments
              programId={program.id}
              programName={program.name}
            />
          ) : (
            <Alert.Root status="info">
              <Alert.Title>Limited access</Alert.Title>
              <Alert.Description>
                You need to be an organization admin to manage enrollments for
                this program.
              </Alert.Description>
            </Alert.Root>
          )}
          <ProgramQuizzes programId={program.id} />
        </Stack>
      </Stack>
    </PageShell>
  )
}
